import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';

import { Form, TextInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';
import { passwordRule } from '@repo/util/passwordRule';
import { createAuthClient, AuthClientError } from '../api/authClient';

const resetPasswordSchema = z
  .object({
    password: passwordRule,
    passwordConfirmation: z.string().min(1, { message: 'Please confirm your password.' }),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'Passwords do not match.',
    path: ['passwordConfirmation'],
  });

type ResetPasswordParams = z.infer<typeof resetPasswordSchema>;

interface ResetPasswordFormProps {
  passwordLoginEndpoint: string;
}

export const ResetPasswordForm = ({ passwordLoginEndpoint }: ResetPasswordFormProps) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authClient = createAuthClient({ passwordLoginEndpoint });

  const form = useZodForm({
    schema: resetPasswordSchema,
    async onSubmit(params: ResetPasswordParams) {
      if (!token) {
        setError('Invalid or missing reset token.');
        return;
      }

      setError(null);
      try {
        await authClient.completePasswordReset(token, params.password, params.passwordConfirmation);
        setSubmitted(true);
      } catch (err) {
        if (err instanceof AuthClientError) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
      }
    },
  });

  if (!token) {
    return (
      <>
        <div className="flex flex-col items-center mb-5 w-full sm:mb-8">
          <h1 className="mb-2.5 sm:mb-5 text-4xl font-bold sm:text-5xl dark:text-blue-100">
            Invalid Reset Link
          </h1>
        </div>

        <div className="flex flex-col items-center px-5 py-8 w-full rounded-xl border xs:px-10 sm:px-16 bg-grey-200 border-grey-400 dark:bg-blue-1000 dark:border-grey-700">
          <p className="mb-6 text-center dark:text-blue-100">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Link to="/forgot-password">
            <Button variant="secondary">Request New Link</Button>
          </Link>
        </div>
      </>
    );
  }

  if (submitted) {
    return (
      <>
        <div className="flex flex-col items-center mb-5 w-full sm:mb-8">
          <h1 className="mb-2.5 sm:mb-5 text-4xl font-bold sm:text-5xl dark:text-blue-100">
            Password Reset Complete
          </h1>
        </div>

        <div className="flex flex-col items-center px-5 py-8 w-full rounded-xl border xs:px-10 sm:px-16 bg-grey-200 border-grey-400 dark:bg-blue-1000 dark:border-grey-700">
          <p className="mb-6 text-center dark:text-blue-100">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <Link to="/login">
            <Button>Go to Login</Button>
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center mb-5 w-full sm:mb-8">
        <h1 className="mb-2.5 sm:mb-5 text-4xl font-bold sm:text-5xl dark:text-blue-100">
          Reset Password
        </h1>
        <p className="text-center dark:text-blue-100">
          {email ? (
            <>
              Enter your new password for <strong>{email}</strong>
            </>
          ) : (
            'Enter your new password below.'
          )}
        </p>
      </div>

      <div className="flex flex-col items-center px-5 py-8 w-full rounded-xl border xs:px-10 sm:px-16 bg-grey-200 border-grey-400 dark:bg-blue-1000 dark:border-grey-700">
        <Form className="flex flex-col gap-6 items-center w-full" {...form}>
          <Form.Submitting>Resetting password...</Form.Submitting>
          {error && <Form.Error autoClose={false}>{error}</Form.Error>}

          <TextInputField
            label="New Password"
            type="password"
            {...registerInput(form, 'password')}
          />

          <TextInputField
            label="Confirm Password"
            type="password"
            {...registerInput(form, 'passwordConfirmation')}
          />

          <p className="text-sm sm:col-span-2 text-grey-600 dark:text-grey-400">
            Password must be at least 12 characters and contain uppercase, lowercase, number, and
            special character.
          </p>

          <div className="flex flex-col items-center mt-4 sm:col-span-2">
            <Button size="md" type="submit" loading={form.formState.isSubmitting}>
              Reset Password
            </Button>
            <Link to="/login">
              <small className="dark:text-blue-300 text-grey-600">Back to Login</small>
            </Link>
          </div>
        </Form>
      </div>
    </>
  );
};
